
import { ArrowBack, Business, Print } from '@mui/icons-material';
import './style.scss';
import { Avatar, Backdrop, Box, Button, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Typography } from '@mui/material';
import { useLanguageContext } from '../../contexts/LanguageContext';
import { useEffect, useMemo, useRef } from 'react';
import IReport from '../../interfaces/IReport';
import { useUserContext } from '../../contexts/UserContext';
import StringHelper from '../../helpers/StringHelper';
import { useEnvironmentContext } from '../../contexts/EnvironmentContext';
import DefaultConstants from '../../data/Constants';
import DateTimeHelper from '../../helpers/DateTimeHelper';
import ReactToPrint from 'react-to-print';
import { useNotificationContext } from '../../contexts/NotificationContext';


interface IReportPreviewProps {
    open: boolean;
    handleClose: () => void;
    report: IReport | undefined;
    filters: any;
}


const ReportPreview: React.FC<IReportPreviewProps> = ({
    open,
    handleClose,
    report,
    filters
}) => {
    const { showLoading, hideLoading } = useNotificationContext();
    const { translate } = useLanguageContext();
    const { user, enterprise } = useUserContext();
    const { environment } = useEnvironmentContext();

    const containerRef = useRef<any>(null);
    const printableRef = useRef<any>(null);
    const onBeforeGetPrintableContentResolve = useRef<any>(null);


    useEffect(() => {
        if (open) {
            containerRef.current.scrollTo({
                top: 0, 
                behavior: 'smooth' 
            });
        }
        
        // eslint-disable-next-line
    }, [open, containerRef]);


    const enterpriseLogo = useMemo(() => {
        const logo = enterprise?.logo.split('?')[0] ?? '';
        const randomText = StringHelper.RandomText(12, false, true, true, false);

        return `${logo}?uniq=${randomText}`;
        // eslint-disable-next-line
    }, [enterprise]);


    const reportTitle = useMemo(() => {
        return report ? translate(report.title) : '';
        // eslint-disable-next-line
    }, [report]);


    return (
        <Backdrop open={open} className='reportpreview-default' ref={containerRef}>
            <Box className='reportpreview-actions'>
                <Box className='reportpreview-close'>
                    <Button onClick={handleClose}>
                        <ArrowBack />
                    </Button>

                    <Typography variant='subtitle1' className='reportpreview-title'>
                        {reportTitle}
                    </Typography>
                </Box>

                <Box className='reportpreview-buttons'>
                    <ReactToPrint
                        content={() => printableRef.current}
                        documentTitle={StringHelper.TrimAll(reportTitle.toLowerCase(), '_')}
                        copyStyles={true}
                        onBeforeGetContent={() => {
                            showLoading();
    
                            return new Promise((resolve) => {
                                onBeforeGetPrintableContentResolve.current = resolve;
                        
                                setTimeout(() => {
                                    hideLoading();
                                    resolve(true);
                                }, 2000);
                            });
                        }}
                        removeAfterPrint={true}
                        trigger={() => {
                            return <Button>
                                <Print />
                            </Button>;
                        }}
                    />
                </Box>
            </Box>

            <Box className='reportpreview-paper' ref={printableRef}>
                <Table>
                    <TableHead className='reportpreview-paper-header'>
                        <TableRow>
                            <TableCell>
                                <Avatar className='reportpreview-paper-header-enterprise' src={enterpriseLogo}>
                                    <Business fontSize='large' />
                                </Avatar>
                                
                                <Typography variant='subtitle1' className='reportpreview-paper-header-title'>
                                    {reportTitle}
                                    
                                    <Typography variant='subtitle2' className='text-color-2'>
                                        {filters?.enterprise?.name ?? enterprise?.name ?? ''}
                                    </Typography>
                                </Typography>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    
                    <TableBody className='reportpreview-paper-content'>
                        <TableRow sx={{ height: '20px' }} />

                        {
                            report && <report.Content {...filters} />
                        }
                    </TableBody>

                    <TableFooter className='reportpreview-paper-footer'>
                        <TableRow>
                            <TableCell>
                                <Typography variant='subtitle1' className='reportpreview-paper-footer-details'>
                                    {
                                        translate('_reportPreviewFooterDetails')
                                            .replace('{date}', DateTimeHelper.Now('DD/MM/YYYY'))
                                            .replace('{hour}', DateTimeHelper.Now('HH:mm:ss'))
                                            .replace('{user}', user?.login ?? '-')
                                    }

                                    <Typography component='h6' variant='subtitle2' className='fontsize-10'>
                                        {translate('developedBy')}
                                        <Typography component='span' variant='subtitle2' className='fontweight-600 fontsize-10'> {DefaultConstants.developer.name} </Typography>
                                    </Typography>
                                </Typography>
                                
                                <img src={environment.partnerPms.images.icon} alt={environment.partnerPms.description} className='reportpreview-paper-footer-partnerpms' />
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>
            </Box>
        </Backdrop>
    );
}

export default ReportPreview;